import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { getCompanyId, isElectricityMetric, isYearOverYearAnomaly, listAnomalies, normalizeListAnomaly } from "../../services/anomalyApi";
import { ANOMALY_THEME } from "./anomalyTheme";

/** Same red as Inactive tab (active-buttonred) on All Configurations */
const ANOMALY_RED = "#cc2229";

const OPEN_SEVERITIES = new Set(["CRITICAL", "HIGH", "MEDIUM"]);

const statusLinkStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: "4px",
  textDecoration: "none",
  cursor: "pointer",
  fontSize: "0.8125rem",
  fontWeight: 500,
  whiteSpace: "nowrap",
};

const ElectricityAnomalyStatus = ({ facility, esgMetric }) => {
  const [hasAnomaly, setHasAnomaly] = useState(false);
  const [loading, setLoading] = useState(false);

  const loadStatus = useCallback(async () => {
    if (!isElectricityMetric(esgMetric) || !facility) {
      setHasAnomaly(false);
      return;
    }
    const customerId = getCompanyId();
    if (!customerId) return;

    setLoading(true);
    try {
      const rows = await listAnomalies(customerId, facility);
      const open = rows
        .map(normalizeListAnomaly)
        .filter(
          (a) =>
            a &&
            !isYearOverYearAnomaly(a) &&
            (a.siteLocation === facility || a.facility === facility) &&
            !a.acknowledged &&
            OPEN_SEVERITIES.has(a.severity)
        );
      setHasAnomaly(open.length > 0);
    } catch (e) {
      console.error("Failed to load anomaly status:", e);
      setHasAnomaly(false);
    } finally {
      setLoading(false);
    }
  }, [facility, esgMetric]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  useEffect(() => {
    const onUpdated = () => loadStatus();
    window.addEventListener("anomaly-data-updated", onUpdated);
    return () => window.removeEventListener("anomaly-data-updated", onUpdated);
  }, [loadStatus]);

  if (!isElectricityMetric(esgMetric)) {
    return <span className="text-muted">—</span>;
  }

  if (loading) {
    return (
      <span style={{ color: "#888", fontSize: "0.875rem" }} aria-busy="true">
        …
      </span>
    );
  }

  const anomalyUrl = `/site-anomalies?sl=${encodeURIComponent(facility)}&esg=${encodeURIComponent(esgMetric)}`;

  if (hasAnomaly) {
    return (
      <Link to={anomalyUrl} style={{ ...statusLinkStyle, color: ANOMALY_RED }} title="View anomalies">
        <WarningAmberIcon sx={{ fontSize: 18, color: ANOMALY_RED }} />
        <span className="record-title" style={{ color: ANOMALY_RED, fontSize: "0.8125rem" }}>
          Anomaly
        </span>
      </Link>
    );
  }

  return (
    <span style={{ ...statusLinkStyle, color: ANOMALY_THEME.primaryTeal, cursor: "default" }}>
      <CheckCircleOutlineIcon sx={{ fontSize: 18, color: ANOMALY_THEME.primaryTeal }} />
      Normal
    </span>
  );
};

export default ElectricityAnomalyStatus;
